class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  isShopAllState — single gate for lifestyle image logic            */
  /* ------------------------------------------------------------------ */
  static isShopAllState(searchParams) {
    if (!searchParams) return true;
    const params = new URLSearchParams(searchParams);
    for (const key of params.keys()) {
      if (key.startsWith('filter.') && key !== 'filter.v.availability') return false;
    }
    return true;
  }

  /* ------------------------------------------------------------------ */
  /*  Core: renderPage                                                  */
  /* ------------------------------------------------------------------ */
  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();

    // Abort any in-flight request
    if (FacetFiltersForm._abortController) {
      FacetFiltersForm._abortController.abort();
    }
    FacetFiltersForm._abortController = new AbortController();
    const { signal } = FacetFiltersForm._abortController;

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}${searchParams ? '&' + searchParams : ''}`;

      fetch(url, { signal })
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          FacetFiltersForm.renderFilters(parsed, event);
          FacetFiltersForm.renderProductGridContainer(parsed, searchParams);
          FacetFiltersForm.renderProductCount(parsed);
          document.dispatchEvent(new CustomEvent('filter:product:grid:updated'));
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          FacetFiltersForm.renderErrorState(searchParams);
          console.error('[facets-multi] Fetch failed:', err);
        });
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  /* ------------------------------------------------------------------ */
  /*  Render: product grid                                              */
  /* ------------------------------------------------------------------ */
  static renderProductGridContainer(parsed, searchParams) {
    const incoming = parsed.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!incoming || !target) return;

    // Strip lifestyle images when a filter is active
    if (!FacetFiltersForm.isShopAllState(searchParams)) {
      incoming.querySelectorAll('[data-lifestyle-image]').forEach((el) => el.remove());
    }

    // Empty-state detection via data-product-card
    const productCards = incoming.querySelectorAll('[data-product-card]');
    const isFiltered = !FacetFiltersForm.isShopAllState(searchParams);

    if (productCards.length === 0 && isFiltered) {
      // True empty — filter returned 0 products, show empty state
      const emptyMarkup = incoming.querySelector('.collection--empty');
      if (!emptyMarkup) {
        // Section didn't render its own empty markup — inject one
        const collection = incoming.querySelector('.collection');
        if (collection) {
          collection.innerHTML =
            '<div class="title-wrapper center" style="padding:4rem 0">' +
            '<h2 class="title title--primary">No products match this filter.</h2>' +
            '</div>';
        }
      }
    }

    target.innerHTML = incoming.innerHTML;
    FacetFiltersForm.executeInlineScripts(target);
  }

  /* ------------------------------------------------------------------ */
  /*  Execute inline scripts after innerHTML replacement                */
  /* ------------------------------------------------------------------ */
  static executeInlineScripts(container) {
    container.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Render: error state                                               */
  /* ------------------------------------------------------------------ */
  static renderErrorState(searchParams) {
    const target = document.getElementById('ProductGridContainer');
    if (!target) return;

    const collection = target.querySelector('.collection');
    if (!collection) return;

    // Don't wipe the grid — show error overlay so user still sees previous content
    let errorEl = collection.querySelector('.collection-error-state');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'collection-error-state';
      errorEl.innerHTML =
        '<p>Something went wrong loading products. Please try again.</p>' +
        '<button type="button" data-retry-filter>RETRY</button>';
      collection.prepend(errorEl);

      errorEl.querySelector('[data-retry-filter]').addEventListener('click', () => {
        errorEl.remove();
        FacetFiltersForm.renderPage(
          searchParams || window.location.search.slice(1),
          null,
          false
        );
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Render: product count                                             */
  /* ------------------------------------------------------------------ */
  static renderProductCount(parsed) {
    const source = parsed.getElementById('ProductCount');
    if (!source) return;

    const count = source.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) container.innerHTML = count;
    if (containerDesktop) containerDesktop.innerHTML = count;
  }

  /* ------------------------------------------------------------------ */
  /*  Render: filters (sidebar + mobile drawer)                         */
  /* ------------------------------------------------------------------ */
  static renderFilters(parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event) {
      FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
    }
  }

  static renderActiveFacets(html) {
    const selectors = ['.active-facets-mobile', '.active-facets-desktop'];
    selectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const selectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    selectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const drawer = mobileForm.closest('menu-drawer');
      if (drawer) drawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSelected = target.querySelector('.facets__selected');
    const sourceSelected = source.querySelector('.facets__selected');
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSelected && targetSelected) {
      targetSelected.outerHTML = sourceSelected.outerHTML;
    }
    if (targetSummary && sourceSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  URL + history                                                     */
  /* ------------------------------------------------------------------ */
  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams ? '?' + searchParams : ''}`
    );
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return grid ? [{ section: grid.dataset.id }] : [];
  }

  /* ------------------------------------------------------------------ */
  /*  Form helpers                                                      */
  /* ------------------------------------------------------------------ */
  createSearchParams(form) {
    const formData = new FormData(form);
    const raw = new URLSearchParams(formData).toString();
    const IN_STOCK = 'filter.v.availability=1';

    // Append availability filter when actual filter params are present
    // (not for sort-only changes which shouldn't restrict availability)
    const hasFilterParam = raw.includes('filter.');
    if (hasFilterParam && !raw.includes(IN_STOCK)) {
      return raw + '&' + IN_STOCK;
    }
    // Strip standalone availability filter when no other filters are present
    if (raw === IN_STOCK) return '';
    return raw;
  }

  onSubmitForm(searchParams, event) {
    // Always reset to page 1 on filter change
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    FacetFiltersForm.renderPage(params.toString(), event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    if (event.target.classList.contains('mobile-facets__checkbox')) {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === 'FacetSortForm' ||
            form.id === 'FacetFiltersForm' ||
            form.id === 'FacetSortDrawerForm'
          ) {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    // Strip standalone availability filter left behind after removing the last real filter
    const params = new URLSearchParams(url);
    const hasRealFilter = Array.from(params.keys()).some(
      (key) => key.startsWith('filter.') && key !== 'filter.v.availability'
    );
    if (!hasRealFilter) params.delete('filter.v.availability');
    url = params.toString();

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm._abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

/* ==================================================================== */
/*  PriceRange                                                          */
/* ==================================================================== */
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

/* ==================================================================== */
/*  FacetRemove                                                         */
/* ==================================================================== */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
